import { useCallback, useEffect, useState } from 'react';
import { NSButton, NSDropdown } from 'bricks';
import PageTitle from 'components/PageTitle/PageTitle';
import { ComparisonGrid } from 'components/ComparisonGrid';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IOptionAnyValue } from 'bricks/types';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import useToast, { ToastType } from 'hooks/useToast';
import './ScenarioComparison.scss';
import useAuthContext from 'contexts/AuthContext';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { useGetBackOfEnvelopeScenarios } from 'views/DealDashboard/BOE/hooks/useGetBOEScenarios';
import { useScenarioContext } from './context/ScenarioComparisonProvider';
import { gridConfig, MAX_SCENARIOS_FOR_PDF } from './constants';

const ScenarioComparisonManager = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { dealId } = useParams<{ dealId: string }>();

    const selectedScenario = location.state?.selectedScenario as BOEScenario;

    const [isDownloading, setIsDownloading] = useState(false);
    const { showToast } = useToast();

    const {
        scenarios, selectedScenarios, addScenario, initializeData, getAvailableOptions, handlePdfExport, selectedScenariosData,
    } = useScenarioContext();

    const { selectedOrganizationId } = useAuthContext();

    const { data: { items: boeScenarios } = {} } = useGetBackOfEnvelopeScenarios({ orgId: selectedOrganizationId!, dealId: Number(dealId) });

    const { data: deal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const handleSelection = useCallback(
        (option: IOptionAnyValue) => {
            const selected = scenarios.find(sc => sc.id === option.value);
            if (selected) {
                addScenario(selected);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [addScenario],
    );

    const handleGoBack = useCallback(() => {
        navigate(`/deal/${dealId}`, { replace: true });
    }, [dealId, navigate]);

    const options = getAvailableOptions();
    const handleExportComparisonPdf = useCallback(
        async () => {
            setIsDownloading(true);
            const toastId = showToast('Downloading PDF...', ToastType.Success, { autoClose: false });
            try {
                if (deal) {
                    await handlePdfExport(deal, selectedScenarios);
                    showToast(
                        'PDF downloaded successfully!',
                        ToastType.Success,
                        { autoClose: 3000 },
                        toastId,
                    );
                }
            } catch {
                showToast(
                    'Failed to download PDF',
                    ToastType.Error,
                    { autoClose: 3000 },
                    toastId,
                );
            } finally {
                setIsDownloading(false);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [handlePdfExport, selectedScenarios],
    );

    const isExportDisabled = selectedScenarios.length > MAX_SCENARIOS_FOR_PDF || selectedScenarios.length === 0;
    const disabledTooltip = selectedScenarios.length > MAX_SCENARIOS_FOR_PDF
        ? `A maximum of ${MAX_SCENARIOS_FOR_PDF} scenarios can be exported`
        : 'No scenarios selected';

    useEffect(() => {
        const allBoeScenarios = boeScenarios?.map(({ id, name }) => ({ name, id })) || [];
        const selectedScenarioOptions = selectedScenario ? [{ label: selectedScenario?.name, value: selectedScenario?.id }] : [];
        initializeData(allBoeScenarios, selectedScenarioOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boeScenarios]);

    return (
        <div className="ScenarioComparison">
            <PageTitle
                title="Scenario comparison"
                subtitle={`Analyzing: ${deal?.dealName}`}
                beta
                betaCustomText="Beta"
            >
                <NSButton text="Exit" color="secondary" outline={false} callback={handleGoBack} className="mr-2" />
                <NSButton
                    id="export-comparison-pdf"
                    text="Export comparison"
                    icon={faDownload}
                    color="primary"
                    outline={false}
                    callback={handleExportComparisonPdf}
                    className="mx-2 heap-deal-back-of-envelope-export-comparison"
                    disabled={isExportDisabled}
                    disabledTooltip={disabledTooltip}
                    isLoading={isDownloading}
                />
            </PageTitle>
            <ComparisonGrid
                gridConfig={
                    { ...gridConfig, data: selectedScenariosData }
                }
                renderAddToComparison={options && options.length > 0 ? () => (
                    <NSDropdown
                        disableOnSingleOption={false}
                        isFullWidth
                        options={options}
                        handleSelectOption={handleSelection}
                        placeholder="Select scenario"
                    />
                ) : null}
                isEditMode
            />
        </div>
    );
};

export default ScenarioComparisonManager;
